import 'twin.macro';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import SimpleNarrowPage from 'components/Layouts/SimpleNarrowPage';
import Img from 'components/Img';
import { Tag } from 'components/Tag';
import { orderByNiveau } from 'utils/orderByNiveau';
import Button from 'components/Buttons/Button';
import { EyeIcon as Eye } from '@heroicons/react/24/outline';
import { PreviewModal } from 'containers/EmploiDuTemps/New/PreviewModal';
import { FilterBar } from 'components/FilterBar';
import ErrorFallback from 'components/Errors/ErrorFallback';
import { useTranslation } from 'react-i18next';
import { useSequenceModeles } from 'containers/Sequences/New/ModelePicker/useSequenceModeles';
import { useCreateSequence } from 'queries/sequences/useCreateSequence';
import _isEmpty from 'lodash-es/isEmpty';
import { sequenceUrls } from 'containers/Sequences/urls';
import {
  StrapiSequence,
  StrapiSequenceCategory,
} from 'containers/Sequences/New/ModelePicker/types';

function getAllUniqCategories(seqs: StrapiSequence[]) {
  const uniqueCategories = new Set();
  const result: StrapiSequenceCategory[] = [];

  for (const seq of seqs) {
    for (const category of seq.categories) {
      if (!uniqueCategories.has(category.name)) {
        uniqueCategories.add(category.name);
        result.push(category);
      }
    }
  }

  return result;
}

export function SequenceTemplatePicker() {
  const { isLoading, data, error } = useSequenceModeles();
  const { t } = useTranslation(['sequence', 'common']);
  const urlSearchParams = new URLSearchParams(location.search);
  const folderIdParam = urlSearchParams.get('folderId') || undefined;
  const folderId = _isEmpty(folderIdParam) ? undefined : folderIdParam;
  const navigate = useNavigate();
  const createSequence = useCreateSequence();
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const seqs: StrapiSequence[] = filter
    ? (data || []).filter(
        prog => !!prog.categories.find(c => c.name === filter),
      )
    : data || [];

  const onCreate = (documentId: string) =>
    createSequence({ templateId: documentId, folderId }).then(seq =>
      navigate(sequenceUrls.sequenceDetails(seq.id)),
    );
  const allCategories = orderByNiveau<StrapiSequenceCategory>(
    getAllUniqCategories(data || []),
    c => c.name,
  );
  if (isLoading) {
    return (
      <div tw="pt-8">
        <SimpleNarrowPage>
          <div tw="space-y-4">
            <div className="tsh-skeleton" tw="h-32" />
            <div className="tsh-skeleton" tw="h-32" />
            <div className="tsh-skeleton" tw="h-32" />
          </div>
        </SimpleNarrowPage>
      </div>
    );
  }
  if (error) {
    return (
      <ErrorFallback
        error={error as any}
        subject="la récupération des modèles"
      />
    );
  }

  return (
    <div tw="pt-8">
      <SimpleNarrowPage>
        <SimpleNarrowPage.Title>
          {t('sequence:chooseSequence')}
        </SimpleNarrowPage.Title>
        <PreviewModal
          url={previewUrl}
          onClose={() => setPreviewUrl(undefined)}
        />
        <FilterBar tw="mb-6 bg-neutral-supershy">
          <div tw="flex flex-wrap gap-x-2 gap-y-2">
            {allCategories.map(category => (
              <Tag
                active={filter === category.name}
                key={category.name}
                onClick={() => {
                  if (filter === category.name) {
                    return setFilter(undefined);
                  }
                  setFilter(category.name);
                }}
                color={category.color}
              >
                {category.name}
              </Tag>
            ))}
          </div>
        </FilterBar>
        <div tw="mb-6 space-y-8">
          {seqs.map(tt => (
            <div key={tt.documentId} tw="bg-white p-6 rounded-xl">
              <div tw="flex gap-x-8">
                <div
                  onClick={() => setPreviewUrl(tt.preview)}
                  tw="shrink-0 cursor-pointer hover:scale-105 transition duration-300 ease-in-out"
                >
                  <Img
                    height={100}
                    width={100}
                    tw="w-full object-cover"
                    s3Path={new URL(tt.preview).pathname}
                  />
                </div>
                <div tw="flex flex-col justify-between flex-grow">
                  <div tw="mb-2">{tt.name}</div>
                  <div tw="space-x-2">
                    {orderByNiveau(tt.categories, () => tt.name).map(cat => (
                      <Tag key={cat.name} color={cat.color}>
                        {cat.name}
                      </Tag>
                    ))}
                  </div>
                  <div tw="text-right flex items-center justify-end gap-x-2">
                    <Button
                      icon={Eye}
                      onClick={() => setPreviewUrl(tt.preview)}
                    >
                      {t('common:buttons.preview')}
                    </Button>
                    <Button
                      intent="primary"
                      aria-label={`Utiliser ${tt.name}`}
                      onClick={() => onCreate(tt.documentId)}
                    >
                      {t('common:buttons.use')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SimpleNarrowPage>
    </div>
  );
}
